/**
 * The agency the signed-in user belongs to.
 *
 * This used to be a chain of increasingly desperate fallbacks: if the user had
 * no agency it promoted them to `admin`, created an agency for them, and — if
 * that failed — searched for ANY agency with no filter at all and attached them
 * to the first row that came back. In a multi-tenant product that last step
 * could put a person inside a stranger's agency, and the two before it are
 * exactly the self-escalation the row rules were changed to stop: a user who
 * can set their own role can set it to "superadmin", and every rule grants that
 * role every agency's data.
 *
 * All three writes are now refused by the database, so the chain could only
 * ever end in a confusing error. It is gone.
 *
 * A signed-in user with no agency is a PROVISIONING FAILURE, not something to
 * paper over. Agencies come from exactly two places, both server-side and both
 * deliberate: `api/auto-provision.js` when someone pays, and `api/create-agency.js`
 * when an operator makes one. If somebody reaches the app without one, the
 * answer is to say so plainly and let it reach support — not to invent an
 * agency and hope it was the right one.
 */

import { pb } from '../lib/pocketbase'

/**
 * @returns the current user's agency id
 * @throws if they are not signed in, or have no agency
 */
export async function ensureUserAgency(): Promise<string> {
  const user = pb.authStore.model
  if (!user) {
    throw new Error('You must be signed in to perform this action.')
  }

  const agencyId = (user as Record<string, unknown>).agency_id
  if (typeof agencyId === 'string' && agencyId) {
    return agencyId
  }

  // Named so it is obvious in Sentry and in a support conversation what went
  // wrong and who it happened to.
  throw new Error(
    'Your account is not attached to an agency yet, so there is nowhere to save this. ' +
    'This is a setup problem on our side rather than anything you did — contact support ' +
    `and quote your account (${(user as Record<string, unknown>).email ?? user.id}).`,
  )
}
